import type { TColumnType } from "components/column-config-views";
import {
  DESIGN_SCOPE_VIEW_META_FIELD_NAME,
  type DesignScopeViewLayout
} from "./design-scope-view-meta";

/** Same field as `ROW_GROUP_META_FIELD_NAME` in column-config-util. */
const ROW_GROUP_META_FIELD_NAME = "__ciq_row_group_meta__";

/** Tree-data group column. Pinned left; not part of the list-view column set. */
export const DESIGN_SCOPE_AUTO_GROUP_COL_ID = "ag-Grid-AutoColumn";

/** Hierarchy-only. Always the column immediately after the grouped system column. */
export const DESIGN_SCOPE_SCOPE_ITEM_COUNT_COL_ID = "scopeItemCount";

const META_FIELD_NAMES = new Set([
  DESIGN_SCOPE_VIEW_META_FIELD_NAME,
  ROW_GROUP_META_FIELD_NAME
]);

const SHARED_TAIL = [
  "designPackage",
  "milestone",
  "lod",
  "bidPackage"
] as const;

function isAutoGroupColId(colId: string): boolean {
  return colId === "group_column" || colId.startsWith("ag-Grid-AutoColumn");
}

/** Column ids in the order the grid defines them for this view. */
export function designScopeDefaultColumnIds(
  layout: DesignScopeViewLayout
): string[] {
  const formatIds =
    layout.view === "hierarchy"
      ? [layout.treeSystem === "UF" ? "masterformat" : "uniformat"]
      : ["uniformat", "masterformat"];
  const leading =
    layout.view === "hierarchy"
      ? [
          DESIGN_SCOPE_AUTO_GROUP_COL_ID,
          DESIGN_SCOPE_SCOPE_ITEM_COUNT_COL_ID,
          "sequence_id",
          "title",
          "description",
          ...formatIds
        ]
      : ["sequence_id", "title", "description", ...formatIds];
  return [...leading, ...SHARED_TAIL];
}

/** Hierarchy count stays beside the grouped UniFormat / MasterFormat column. */
function placeScopeItemCountAfterGroup(ordered: string[]): string[] {
  const withoutCount = ordered.filter(
    (id) => id !== DESIGN_SCOPE_SCOPE_ITEM_COUNT_COL_ID
  );
  const groupIndex = withoutCount.indexOf(DESIGN_SCOPE_AUTO_GROUP_COL_ID);
  const insertAt = groupIndex === -1 ? 0 : groupIndex + 1;
  withoutCount.splice(insertAt, 0, DESIGN_SCOPE_SCOPE_ITEM_COUNT_COL_ID);
  return withoutCount;
}

function blankColumn(name: string, order: number): TColumnType {
  return {
    order,
    visibility: true,
    field: {
      name,
      agFilter: null,
      filter: null,
      sortOrder: null,
      range: null,
      width: null
    }
  };
}

/**
 * Saved order belongs to whichever view last wrote it. Columns that exist only
 * in the other view must not hide or shift the current view: keep the relative
 * order of shared columns, and insert view-only columns at their default slot.
 */
export function reconcileDesignScopeColumnList(
  layout: DesignScopeViewLayout,
  columnList: TColumnType[]
): TColumnType[] {
  const named = columnList.filter((col) => col.field?.name);
  if (named.length === 0) return columnList;

  const defaultIds = designScopeDefaultColumnIds(layout);
  const defaultIdSet = new Set(defaultIds);

  const savedByName = new Map<string, TColumnType>();
  named.forEach((col) => {
    const name = col.field.name;
    const key = isAutoGroupColId(name) ? DESIGN_SCOPE_AUTO_GROUP_COL_ID : name;
    if (savedByName.has(key)) return;
    savedByName.set(key, {
      ...col,
      field: { ...col.field, name: key }
    });
  });

  const savedOrder = [...named]
    .sort((a, b) => a.order - b.order)
    .map((col) =>
      isAutoGroupColId(col.field.name)
        ? DESIGN_SCOPE_AUTO_GROUP_COL_ID
        : col.field.name
    )
    .filter(
      (name, index, all) =>
        defaultIdSet.has(name) && all.indexOf(name) === index
    );

  const ordered = [...savedOrder];
  defaultIds.forEach((id) => {
    if (ordered.includes(id)) return;
    if (id === DESIGN_SCOPE_AUTO_GROUP_COL_ID) {
      ordered.unshift(id);
      return;
    }
    const defaultIndex = defaultIds.indexOf(id);
    let insertAt = ordered.length;
    for (let i = defaultIndex - 1; i >= 0; i -= 1) {
      const idx = ordered.indexOf(defaultIds[i]);
      if (idx !== -1) {
        insertAt = idx + 1;
        break;
      }
    }
    if (insertAt === ordered.length) {
      for (let i = defaultIndex + 1; i < defaultIds.length; i += 1) {
        const idx = ordered.indexOf(defaultIds[i]);
        if (idx !== -1) {
          insertAt = idx;
          break;
        }
      }
    }
    ordered.splice(insertAt, 0, id);
  });

  const placed =
    layout.view === "hierarchy"
      ? placeScopeItemCountAfterGroup(ordered)
      : ordered;

  const reconciled = placed.map((name, index) => {
    const existing = savedByName.get(name);
    if (existing) return { ...existing, order: index + 1 };
    return blankColumn(name, index + 1);
  });

  const meta = named
    .filter((col) => META_FIELD_NAMES.has(col.field.name))
    .map((col, index) => ({
      ...col,
      order: reconciled.length + index + 1
    }));

  return [...reconciled, ...meta];
}
